// Supabase Client Configuration
// 
// To use Supabase, you need to:
// 1. Create a free project at https://supabase.com
// 2. Copy your project URL and anon key from Settings > API
// 3. Update the values below

import { createClient } from '@supabase/supabase-js'
import type { Database } from '../src/types/database.types'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'YOUR_SUPABASE_URL'
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY'

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY)

// Type definitions for our data models
export interface Client {
    id: string
    name: string
    /** Contact email for the building (facilities manager, etc.). Nullable — not every building has one at creation time. Used by Trace/Signal for outbound emails. */
    email: string | null
    company_id: string
    phone: string | null
    primary_contact: string | null
    notes: string | null
    created_at: string | null
    banner_url: string | null
    logo_url: string | null
    /** When true, client is shown only on demo.luster.company; when false, only on portal. */
    is_demo: boolean
}

export interface Property {
    id: string
    client_id: string | null
    name: string
    address: string
    property_type: string | null
    square_footage: number | null
    special_instructions: string | null
    latitude: number | null
    longitude: number | null
    geofence_radius_meters: number | null
    created_at: string | null
}

import type { GeminiSupplyAnalysis } from './geminiSupplyAnalysisSchema'
export type { GeminiSupplyAnalysis } from './geminiSupplyAnalysisSchema'

export interface CleanReport {
    id: string
    property_id: string | null
    title: string
    activity_date: string
    status: string | null
    service_type: string | null
    operator_name: string | null
    checklist_notes: string | null
    /** Stored as Json in DB; cast to ChecklistData when reading. */
    checklist: {
        [category: string]: {
            tasks: {
                task: string
                completed: boolean
            }[]
        }
    } | null
    photos: string[] | null
    created_at: string | null
    // Supply Synapse
    supply_snap_photo: string | null
    supply_snap_estimate: number | null
    supply_snap_room: string | null
    site_sense_audio_url: string | null
    gemini_analysis: GeminiSupplyAnalysis | null
    analysis_status: 'pending' | 'completed' | 'failed' | null
    time_entry_id: string | null
}

export interface TimeEntry {
    id: string
    operator_id: string
    property_id: string | null
    clock_in: string
    clock_out: string | null
    clock_in_lat: number | null
    clock_in_lng: number | null
    clock_out_lat: number | null
    clock_out_lng: number | null
    clock_in_accuracy: number | null
    clock_out_accuracy: number | null
    geofence_status_in: 'inside' | 'outside' | 'unknown' | null
    geofence_status_out: 'inside' | 'outside' | 'unknown' | null
    duration_minutes: number | null
    notes: string | null
    is_demo: boolean
    created_at: string
}

export interface ProtocolTask {
    task: string
    instructions?: string
}

export interface ProtocolCategory {
    tasks: ProtocolTask[]
}

export interface ServiceProtocol {
    id: string
    company_id: string | null
    name: string
    description: string | null
    /** Stored as Json in DB; cast to Record<string, ProtocolCategory> when reading. */
    categories: Record<string, ProtocolCategory>
    is_default: boolean | null
    is_demo: boolean | null
    created_by: string | null
    created_at: string | null
    updated_at: string | null
}

// Helper to check if Supabase is configured
export function isSupabaseConfigured(): boolean {
    return SUPABASE_URL !== 'YOUR_SUPABASE_URL' && SUPABASE_ANON_KEY !== 'YOUR_SUPABASE_ANON_KEY'
}

/** Report photos bucket (public). Use uploadReportPhoto to upload and get public URL. */
export const REPORT_PHOTOS_BUCKET = 'report-photos'

/** Path prefix for client dashboard banners in report-photos bucket. */
const CLIENT_BANNERS_PREFIX = 'client-banners'

/**
 * Upload a client dashboard banner image; returns public URL.
 * Store the returned URL in clients.banner_url. Path: client-banners/{clientId}/{timestamp}-{filename}
 */
export async function uploadClientBanner(file: File, clientId: string): Promise<string> {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(-60)
    const path = `${CLIENT_BANNERS_PREFIX}/${clientId}/${Date.now()}-${safeName}`
    const { data, error } = await supabase.storage.from(REPORT_PHOTOS_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
    })
    if (error) throw error
    const { data: urlData } = supabase.storage.from(REPORT_PHOTOS_BUCKET).getPublicUrl(data.path)
    return urlData.publicUrl
}

/**
 * Upload a file to report-photos bucket and return its public URL.
 * Path format: reports/{reportId or 'draft'}/{timestamp}-{safeName}
 */
export async function uploadReportPhoto(
    file: File,
    options?: { reportId?: string; prefix?: string }
): Promise<string> {
    const prefix = options?.prefix ?? options?.reportId ?? 'draft'
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(-80)
    const path = `${prefix}/${Date.now()}-${safeName}`
    const { data, error } = await supabase.storage.from(REPORT_PHOTOS_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
    })
    if (error) throw error
    const { data: urlData } = supabase.storage.from(REPORT_PHOTOS_BUCKET).getPublicUrl(data.path)
    return urlData.publicUrl
}

/** Path prefix for report audio (Site Sense) in report-photos bucket. */
const REPORT_AUDIO_PREFIX = 'audio'

/** Path for profile avatars (report-photos bucket). identifier = clientId (client) or auth user id (admin/operator). */
const PROFILE_AVATARS_PREFIX = 'profile-avatars'
/** Path for dashboard banners (admin); identifier = auth user id. */
const PROFILE_BANNERS_PREFIX = 'profile-banners'

/**
 * Upload a profile avatar image; returns public URL.
 * Store in clients.logo_url (client) or user_metadata.avatar_url (admin/operator).
 */
export async function uploadProfileAvatar(file: File, identifier: string): Promise<string> {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(-60)
    const path = `${PROFILE_AVATARS_PREFIX}/${identifier}/${Date.now()}-${safeName}`
    const { data, error } = await supabase.storage.from(REPORT_PHOTOS_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
    })
    if (error) throw error
    const { data: urlData } = supabase.storage.from(REPORT_PHOTOS_BUCKET).getPublicUrl(data.path)
    return urlData.publicUrl
}

/**
 * Upload a dashboard banner (e.g. for admin); returns public URL.
 * Store in user_metadata.dashboard_banner_url.
 */
export async function uploadProfileBanner(file: File, userId: string): Promise<string> {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(-60)
    const path = `${PROFILE_BANNERS_PREFIX}/${userId}/${Date.now()}-${safeName}`
    const { data, error } = await supabase.storage.from(REPORT_PHOTOS_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
    })
    if (error) throw error
    const { data: urlData } = supabase.storage.from(REPORT_PHOTOS_BUCKET).getPublicUrl(data.path)
    return urlData.publicUrl
}

/** Path for portal branding (report-photos bucket). */
const BRANDING_LOGO_PREFIX = 'branding'

/**
 * Upload a file to portal branding (report-photos bucket).
 * Store in portal_branding columns.
 */
export async function uploadBrandingFile(file: File, type: 'logo' | 'banner'): Promise<string> {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(-60)
    const path = `${BRANDING_LOGO_PREFIX}/${type}-${Date.now()}-${safeName}`
    const { data, error } = await supabase.storage.from(REPORT_PHOTOS_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
    })
    if (error) throw error
    const { data: urlData } = supabase.storage.from(REPORT_PHOTOS_BUCKET).getPublicUrl(data.path)
    return urlData.publicUrl
}

/**
 * Upload portal branding logo; returns public URL.
 * Store in portal_branding.logo_url. Only creator should call this.
 */
export async function uploadBrandingLogo(file: File): Promise<string> {
    return uploadBrandingFile(file, 'logo')
}

/** Path prefix for update media (images, gifs, video embeds in rich text). */
const UPDATE_MEDIA_PREFIX = 'update-media'

/**
 * Upload a media file attached to a portal update (image/gif/video for rich text body).
 * Path: update-media/{updateId|'draft'}/{timestamp}-{safeName}
 */
export async function uploadUpdateMedia(
    file: File,
    updateId?: string
): Promise<string> {
    const prefix = updateId ?? 'draft'
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(-80)
    const path = `${UPDATE_MEDIA_PREFIX}/${prefix}/${Date.now()}-${safeName}`
    const { data, error } = await supabase.storage.from(REPORT_PHOTOS_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
    })
    if (error) throw error
    const { data: urlData } = supabase.storage.from(REPORT_PHOTOS_BUCKET).getPublicUrl(data.path)
    return urlData.publicUrl
}

/**
 * Upload an audio blob (e.g. from MediaRecorder) and return its public URL.
 * Uses report-photos bucket with path audio/{prefix}/{timestamp}.webm.
 * Ensure the bucket allows audio MIME types (e.g. audio/webm).
 */
export async function uploadReportAudio(
    blob: Blob,
    options?: { prefix?: string }
): Promise<string> {
    const prefix = options?.prefix ?? 'operator'
    const path = `${REPORT_AUDIO_PREFIX}/${prefix}/${Date.now()}.webm`
    const { data, error } = await supabase.storage.from(REPORT_PHOTOS_BUCKET).upload(path, blob, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'audio/webm',
    })
    if (error) throw error
    const { data: urlData } = supabase.storage.from(REPORT_PHOTOS_BUCKET).getPublicUrl(data.path)
    return urlData.publicUrl
}
